import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  const readCartCount = () => {
    try {
      const raw = localStorage.getItem('cart');
      if (raw) {
        const parsed = JSON.parse(raw) as { quantity: number }[];
        if (Array.isArray(parsed)) {
          return parsed.reduce((sum, item) => sum + (item.quantity || 0), 0);
        }
      }
    } catch {
      // ignore
    }
    return 0;
  };

  useEffect(() => {
    setCartCount(readCartCount());

    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);

    const handleStorage = () => setCartCount(readCartCount());
    window.addEventListener('storage', handleStorage);

    // Poll for same-tab localStorage changes
    const interval = setInterval(() => setCartCount(readCartCount()), 500);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setSearchOpen(false);
    }
  };

  const navLinks = [
    { label: 'Home', to: '/' },
    { label: 'Shop', to: '/shop' },
    { label: 'Contact', to: '/contact' },
    { label: 'FAQ', to: '/faq' },
  ];

  const isTransparent = isHome && !scrolled;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 overflow-x-hidden ${scrolled || !isHome ? 'bg-white/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'}`}>
      <nav className="max-w-7xl mx-auto px-4 md:px-6 py-4">
        <div className="flex items-center justify-between gap-2 md:gap-4">
          <Link to="/" className="flex-shrink-0">
            <h1 className={`text-xl md:text-3xl font-black transition-all duration-300 ${isTransparent ? 'text-white drop-shadow-lg' : 'bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent'}`}>
              AISHOPIO
            </h1>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to} className={`font-medium transition-all duration-300 hover:scale-105 whitespace-nowrap ${isTransparent ? 'text-white hover:text-orange-300 drop-shadow-md' : location.pathname === link.to ? 'text-orange-500 font-semibold' : 'text-gray-800 hover:text-orange-500'}`}>
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <div className="relative hidden md:block">
              {searchOpen ? (
                <form onSubmit={handleSearch} className="flex items-center">
                  <input autoFocus type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search products..." className="w-48 px-4 py-2 rounded-full text-sm border border-orange-300 focus:outline-none focus:border-orange-500 bg-white text-gray-800" />
                  <button type="submit" className="ml-2 w-8 h-8 flex items-center justify-center text-orange-500 cursor-pointer"><i className="ri-search-line text-lg"></i></button>
                  <button type="button" onClick={() => setSearchOpen(false)} className="w-8 h-8 flex items-center justify-center text-gray-500 cursor-pointer"><i className="ri-close-line text-lg"></i></button>
                </form>
              ) : (
                <button onClick={() => setSearchOpen(true)} className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 cursor-pointer ${isTransparent ? 'text-white hover:bg-white/20' : 'text-gray-700 hover:bg-orange-50 hover:text-orange-500'}`}>
                  <i className="ri-search-line text-xl"></i>
                </button>
              )}
            </div>

            <Link to="/cart" className={`relative w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 cursor-pointer flex-shrink-0 ${isTransparent ? 'text-white hover:bg-white/20' : 'text-gray-700 hover:bg-orange-50 hover:text-orange-500'}`}>
              <i className="ri-shopping-cart-line text-xl"></i>
              {cartCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-orange-500 to-pink-600 text-white text-xs rounded-full flex items-center justify-center font-bold">{cartCount > 99 ? '99+' : cartCount}</span>}
            </Link>

            <Link to="/shop" className={`hidden md:flex px-4 py-2.5 rounded-full font-semibold transition-all duration-300 hover:scale-105 hover:shadow-xl whitespace-nowrap cursor-pointer text-sm ${isTransparent ? 'bg-white/20 backdrop-blur-md text-white border-2 border-white/50 hover:bg-white/30' : 'bg-gradient-to-r from-orange-500 to-pink-600 text-white hover:shadow-orange-500/50'}`}>
              Shop Now
            </Link>

            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-300 cursor-pointer flex-shrink-0 ${isTransparent ? 'bg-white/20 backdrop-blur-md text-white' : 'bg-gray-100 text-gray-800'}`}>
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        <div className={`md:hidden overflow-hidden transition-all duration-300 ${mobileMenuOpen ? 'max-h-96 mt-4' : 'max-h-0'}`}>
          <div className={`flex flex-col gap-2 p-4 rounded-2xl ${isTransparent ? 'bg-white/10 backdrop-blur-lg border border-white/20' : 'bg-gray-50'}`}>
            <form onSubmit={handleSearch} className="flex items-center gap-2 mb-2">
              <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search products..." className="flex-1 min-w-0 px-4 py-2 rounded-full text-sm border border-orange-300 focus:outline-none bg-white text-gray-800" />
              <button type="submit" className="w-9 h-9 flex-shrink-0 flex items-center justify-center bg-gradient-to-r from-orange-500 to-pink-600 text-white rounded-full cursor-pointer"><i className="ri-search-line"></i></button>
            </form>
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to} onClick={() => setMobileMenuOpen(false)} className={`px-4 py-3 rounded-lg text-left font-medium text-sm transition-all duration-300 cursor-pointer ${isTransparent ? 'text-white hover:bg-white/20' : 'text-gray-800 hover:bg-orange-50 hover:text-orange-500'}`}>
                {link.label}
              </Link>
            ))}
            <Link to="/shop" onClick={() => setMobileMenuOpen(false)} className="px-4 py-3 rounded-lg bg-gradient-to-r from-orange-500 to-pink-600 text-white font-semibold transition-all duration-300 hover:shadow-lg whitespace-nowrap cursor-pointer text-center text-sm">
              Shop Now
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
}
